// src/pages/SubscribePage.tsx
import { useState, useEffect } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from '@/firebase';
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/firebase";
import { useSubscription } from "../hooks/useSubscription";
import { useBillingSummary } from "@/features/billing/useBillingSummary";
import { useAuthState, useAuthActions } from "../auth/AuthProvider";
import { mapSignupError } from "@/lib/authErrors";
import { createCheckoutSessionHttp } from "@/lib/cf";
import { logEvent } from "@/lib/analytics.safe";
import { safeAssignCheckoutUrl } from "@/billing/checkout";
import { useRouteTitle } from "../hooks/useRouteTitle";
import toast from "react-hot-toast";

type Plan = "monthly" | "annual";

// Do NOT restrict TLD length or maintain a TLD allowlist.
// Modern TLDs (.ai, .legal, .technology, etc.) are valid.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const isValidEmail = (email: string): boolean => {
  return EMAIL_RE.test(email.trim());
};

const disallowedReturnPaths = new Set(["/login", "/signup", "/reset-password"]);

const sanitizeReturnTo = (value: string | null): string => {
  if (!value) return "/cases";
  if (!value.startsWith("/")) return "/cases";
  const base = value.split("?")[0];
  if (disallowedReturnPaths.has(base)) return "/cases";
  return value;
};

const buildCheckoutTargets = (returnTo: string) => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  // Billing-first flow: success → /subscribe/success (mints claim) → /signup?claim=...
  const successUrl = `${origin}/subscribe/success`;
  const cancelUrl = `${origin}/subscribe`;
  return { successUrl, cancelUrl };
};

const logClientError = (...args: unknown[]) => {
  // Suppress error logging in test environment (build-time gated)
  if (import.meta.env.MODE === 'test') {
    return;
  }
  console.error(...args);
};

export default function SubscribePage() {
  useRouteTitle("Subscribe");
  const { user } = useAuthState();
  const { signUp } = useAuthActions();
  const navigate = useNavigate();
  const { status, plan, currentPeriodEnd, loading: subscriptionLoading } = useSubscription();
  const { customerId, loading: billingLoading } = useBillingSummary();
  const [sp] = useSearchParams();
  const [email, setEmail] = useState("");
  const [selectedPlan, setSelectedPlan] = useState<Plan>("monthly");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Inline registration form state
  const [showRegForm, setShowRegForm] = useState(false);
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regError, setRegError] = useState<string | null>(null);
  const [regLoading, setRegLoading] = useState(false);

  // Trial status from Firestore top-level fields (distinct from Stripe subscription sub-doc)
  const [trialInfo, setTrialInfo] = useState<{
    isTrialing: boolean;
    isExpired: boolean;
    daysRemaining: number | null;
  }>({ isTrialing: false, isExpired: false, daysRemaining: null });

  useEffect(() => {
    if (!user?.uid) return;
    const ref = doc(db, 'users', user.uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const topStatus = snap.get('subscriptionStatus') as string | undefined;
      const trialEndsAt = snap.get('trialEndsAt');
      if (topStatus === 'trialing' || topStatus === 'trial_expired') {
        const endsAtMs: number =
          typeof trialEndsAt?.toMillis === 'function' ? trialEndsAt.toMillis() : 0;
        const active = topStatus === 'trialing' && Date.now() < endsAtMs;
        const days = active
          ? Math.ceil((endsAtMs - Date.now()) / (1000 * 60 * 60 * 24))
          : null;
        setTrialInfo({ isTrialing: active, isExpired: !active, daysRemaining: days });
      }
    });
    return unsub;
  }, [user?.uid]);

  const handleCreateAccount = async () => {
    setRegError(null);
    if (!regEmail.trim() || !regPassword) {
      setRegError("Email and password are required.");
      return;
    }
    if (!isValidEmail(regEmail)) {
      setRegError("Please enter a valid email address.");
      return;
    }
    setRegLoading(true);
    try {
      await signUp(regEmail.trim(), regPassword, regName.trim() || undefined);
      navigate("/verify-email");
    } catch (err) {
      setRegError(mapSignupError(err));
    } finally {
      setRegLoading(false);
    }
  };

  const returnTo = sanitizeReturnTo(sp.get("returnTo") ?? sp.get("next"));
  const reason = sp.get("reason"); // e.g. "trial_expired"
  // Expired if URL signals it OR Firestore confirms it
  const isTrialExpired = reason === "trial_expired" || trialInfo.isExpired;

  // CRITICAL: Check both Firestore status AND Stripe customer ID
  // Prevents stale Firestore data from blocking navigation to subscribe page
  const hasValidSubscription = !subscriptionLoading && status === "active" && !billingLoading && customerId;
  
  if (hasValidSubscription) {
    return <Navigate to={returnTo} replace />;
  }

  // Also require Stripe customer ID for displaying "active" status
  // If Firestore shows "active" but no Stripe customer → show subscribe form
  const shouldShowActiveStatus = hasValidSubscription;

  const handleSubscribe = async () => {
    // For authenticated users, use their account email
    // For unauthenticated users, require email input
    if (!user && !email.trim()) {
      setError("Email is required.");
      return;
    }

    if (!user && !isValidEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { successUrl, cancelUrl } = buildCheckoutTargets(returnTo);

      logEvent("billing_checkout_started", {
        event_category: "billing",
        plan: selectedPlan,
        flow: user ? "srl_resubscribe" : "srl_subscribe"
      });

      // If user is authenticated, don't pass email (backend will use auth user's email)
      // If user is unauthenticated, pass email for checkout session
      const { url } = await createCheckoutSessionHttp({
        plan: selectedPlan,
        ...(user ? {} : { email: email.trim() }), // Only include email if not authenticated
        successUrl,
        cancelUrl,
        returnUrl: successUrl,
      });

      safeAssignCheckoutUrl(url);
      // Redirect will happen automatically via safeAssignCheckoutUrl
    } catch (err) {
      logClientError("Error creating checkout session:", err);
      let errorMessage = "Billing couldn't start. Please try again.";
      
      if (err instanceof Error) {
        // Show specific error messages from backend
        const msg = err.message;
        if (msg.includes('promo code')) {
          errorMessage = msg; // "That promo code isn't valid."
        } else if (msg.includes('email')) {
          errorMessage = msg; // "Enter a valid email address."
        } else if (msg.includes('plan')) {
          errorMessage = msg; // "Invalid subscription plan selected."
        } else if (!msg.includes('Billing couldn\'t start')) {
          // If backend sent a specific message we haven't handled, use it
          errorMessage = msg;
        }
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setLoading(true);
    setError(null);

    try {
      // eslint-disable-next-line @typescript-eslint/no-empty-object-type
      const createBillingPortalSession = httpsCallable<{}, { url: string }>(functions, "createBillingPortalSession");
      const result = await createBillingPortalSession({});

      if (result.data.url) {
        window.location.href = result.data.url;
      } else {
        throw new Error("No billing portal URL returned");
      }
    } catch (err) {
      logClientError("Error creating billing portal session:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to open billing portal. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  // Show "Status: Active" view ONLY if both Firestore and Stripe agree subscription is active
  if (shouldShowActiveStatus) {
    return (
      <div className="min-h-screen bg-transparent px-6 py-10 text-gray-100">
        <div className="mx-auto w-full max-w-2xl rounded-2xl border border-gray-700 bg-slate-700 p-8 shadow-xl">
          <h1 className="text-center text-3xl font-semibold">Your Subscription</h1>
          <div className="mt-6 space-y-4 text-gray-300">
            <div className="rounded-2xl border border-gray-600 bg-slate-800 p-6">
              <p className="text-lg">
                <span className="font-semibold text-gray-100">Status:</span>{" "}
                <span className="font-medium text-green-500">Active</span>
              </p>
              {plan && (
                <p className="text-lg">
                  <span className="font-semibold text-gray-900">Plan:</span>{" "}
                  <span className="capitalize text-gray-900">{plan}</span>
                </p>
              )}
              {currentPeriodEnd && (
                <p className="text-lg">
                  <span className="font-semibold text-gray-900">Renews:</span>{" "}
                  {currentPeriodEnd.toLocaleDateString()}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={handleManageBilling}
              disabled={loading}
              className="w-full rounded-2xl bg-brand-orange px-6 py-3 text-lg font-semibold text-white transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-brand-orange/20 disabled:opacity-60"
            >
              {loading ? "Loading…" : "Manage billing"}
            </button>
            <Link
              to="/cases"
              className="block w-full rounded-2xl border border-gray-700 px-6 py-3 text-center text-lg font-medium text-gray-100 transition hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-orange/20"
            >
              Back to dashboard
            </Link>
          </div>

          {error && (
            <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700" role="alert">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent px-6 py-10 text-white">
      <div className="mx-auto max-w-5xl">
        <div className="text-center">
          <h1 className="text-3xl font-semibold text-white">
            {isTrialExpired ? "Your Free Trial Has Ended" : "Subscribe to ThreadLock"}
          </h1>
          <p className="mt-2 text-slate-300">
            {isTrialExpired
              ? "Your 7-day free trial has expired. Subscribe below to regain access to your cases."
              : trialInfo.isTrialing
              ? `You have ${trialInfo.daysRemaining} day${trialInfo.daysRemaining === 1 ? '' : 's'} left in your free trial. Subscribe now to ensure uninterrupted access.`
              : "An active subscription is required to access your cases and use ThreadLock."}
          </p>
        </div>

        {/* Trial callouts — shown based on live Firestore state */}
        {trialInfo.isTrialing && trialInfo.daysRemaining !== null && (
          <div className="mx-auto mt-6 max-w-xl rounded-xl border border-brand-orange/40 bg-brand-orange/10 px-4 py-3 text-sm text-orange-200">
            <span className="font-semibold text-white">Free trial active</span> —{" "}
            {trialInfo.daysRemaining} day{trialInfo.daysRemaining === 1 ? "" : "s"} remaining.
            {" "}Subscribe before your trial ends to keep access to all your cases.
          </div>
        )}
        {isTrialExpired && (
          <div className="mx-auto mt-6 max-w-xl rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            <span className="font-semibold text-white">Your 7-day free trial has ended.</span>{" "}
            Subscribe below to restore access to your cases and documents.
          </div>
        )}
        {!user && !isTrialExpired && (
          <div className="mx-auto mt-6 max-w-xl">
            {!showRegForm ? (
              <div className="rounded-xl border border-slate-600 bg-slate-800/60 px-4 py-3 text-center text-sm text-slate-300">
                New to ThreadLock?{" "}
                <button
                  type="button"
                  onClick={() => setShowRegForm(true)}
                  className="font-semibold text-brand-orange underline hover:opacity-80"
                >
                  Create a free account
                </button>{" "}
                and start your <span className="text-white">7-day free trial</span> — no credit card required.
              </div>
            ) : (
              <div className="rounded-xl border border-slate-600 bg-slate-800 px-6 py-5">
                <h2 className="text-lg font-semibold text-white">Create your free account</h2>
                <p className="mt-1 text-sm text-slate-400">7-day free trial — no credit card required.</p>

                <form
                  onSubmit={(e) => { e.preventDefault(); handleCreateAccount(); }}
                  noValidate
                >
                  <div className="mt-4 space-y-3">
                    <div>
                      <label className="mb-1 block text-sm text-slate-300" htmlFor="reg-name">
                        Full name
                      </label>
                      <input
                        id="reg-name"
                        type="text"
                        value={regName}
                        onChange={(e) => setRegName(e.target.value)}
                        placeholder="Your name"
                        className="w-full rounded-xl border border-gray-700 bg-slate-900 px-3 py-2 text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-orange"
                        autoComplete="name"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm text-slate-300" htmlFor="reg-email">
                        Email address
                      </label>
                      <input
                        id="reg-email"
                        type="email"
                        value={regEmail}
                        onChange={(e) => setRegEmail(e.target.value)}
                        placeholder="your@email.com"
                        required
                        className="w-full rounded-xl border border-gray-700 bg-slate-900 px-3 py-2 text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-orange"
                        autoComplete="email"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm text-slate-300" htmlFor="reg-password">
                        Password
                      </label>
                      <input
                        id="reg-password"
                        type="password"
                        value={regPassword}
                        onChange={(e) => setRegPassword(e.target.value)}
                        placeholder="At least 6 characters"
                        required
                        className="w-full rounded-xl border border-gray-700 bg-slate-900 px-3 py-2 text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-orange"
                        autoComplete="new-password"
                      />
                    </div>
                  </div>

                  {regError && (
                    <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300" role="alert">
                      {regError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={regLoading || !regEmail || !regPassword}
                    className="mt-4 w-full rounded-xl bg-brand-orange px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-brand-orange/20 disabled:opacity-60"
                  >
                    {regLoading ? "Creating account…" : "Start free trial"}
                  </button>
                </form>

                <p className="mt-3 text-center text-xs text-slate-400">
                  Already have an account?{" "}
                  <Link to="/login" className="text-brand-orange underline hover:opacity-80">
                    Sign in
                  </Link>
                </p>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="mx-auto mt-6 max-w-xl rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700" role="alert">
            {error}
          </div>
        )}

        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-4 md:grid-cols-2">
          <button
            type="button"
            onClick={() => setSelectedPlan("monthly")}
            className={`rounded-2xl border px-5 py-6 text-left transition hover:shadow ${
              selectedPlan === "monthly"
                ? "border-brand-orange bg-brand-orange/10 ring-2 ring-brand-orange"
                : "border-gray-700 bg-slate-800"
            }`}
            aria-pressed={selectedPlan === "monthly"}
          >
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold text-white">Monthly</div>
              <div className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${selectedPlan === "monthly" ? "border-brand-orange" : "border-gray-700"}`}>
                <div className={`h-2.5 w-2.5 rounded-full ${selectedPlan === "monthly" ? "bg-brand-orange" : "bg-transparent"}`} />
              </div>
            </div>
            <div className="mt-2 text-3xl font-bold text-white">
              $29<span className="ml-1 text-base font-medium text-slate-300">/month</span>
            </div>
            <ul className="mt-3 space-y-1 text-sm text-slate-300">
              <li>✔ Full access to all features</li>
              <li>✔ Cancel anytime</li>
            </ul>
          </button>

          <button
            type="button"
            onClick={() => setSelectedPlan("annual")}
            className={`rounded-2xl border px-5 py-6 text-left transition hover:shadow ${
              selectedPlan === "annual"
                ? "border-brand-orange bg-brand-orange/10 ring-2 ring-brand-orange"
                : "border-gray-700 bg-slate-800"
            }`}
            aria-pressed={selectedPlan === "annual"}
          >
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold text-white">Annual</div>
              <div className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${selectedPlan === "annual" ? "border-brand-orange" : "border-gray-700"}`}>
                <div className={`h-2.5 w-2.5 rounded-full ${selectedPlan === "annual" ? "bg-brand-orange" : "bg-transparent"}`} />
              </div>
            </div>
            <div className="mt-2 text-3xl font-bold text-white">
              $290<span className="ml-1 text-base font-medium text-slate-300">/year</span>
            </div>
            <ul className="mt-3 space-y-1 text-sm text-slate-300">
              <li>✔ Includes SmartBox™ (Patent Pending)</li>
              <li>✔ Full access to all features</li>
            </ul>
          </button>
        </div>

        {/* Only show email input for unauthenticated users */}
        {!user && (
          <div className="mx-auto mt-6 max-w-xl">
            <label className="mb-1 block text-sm text-slate-300" htmlFor="email">
              Email address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="w-full rounded-xl border border-gray-700 bg-slate-800 px-3 py-2 text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-orange"
              autoComplete="email"
            />
          </div>
        )}

        <div className="mx-auto mt-4 max-w-xl">
          <button
            type="button"
            onClick={handleSubscribe}
            disabled={loading || subscriptionLoading || (!user && !email)}
            className="w-full rounded-2xl bg-brand-orange px-4 py-3 text-lg font-semibold text-white transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-brand-orange/20 disabled:opacity-60"
          >
            {loading ? "Starting checkout…" : selectedPlan === "annual" ? "Subscribe annually" : "Subscribe monthly"}
          </button>
          <p className="mt-2 text-center text-xs text-gray-500">
            Secure payment processing via Stripe
          </p>

          <div className="mt-6 flex items-center justify-center gap-6 text-sm">
            <Link className="text-slate-400 underline transition hover:text-white" to="/login">
              Return to login
            </Link>
            <Link className="text-slate-400 underline transition hover:text-white" to="/cases">
              Cancel
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
